import React, { useState, useEffect } from 'react';
import {
  Shield,
  Search,
  Filter,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  Flag,
  Trash2,
  Eye,
  Calendar,
  User,
  Star,
  AlertCircle,
  RefreshCw,
  BookOpen,
  Building
} from 'lucide-react';
import moderationService, { 
  ModerationStats, 
  TrainingReview, 
  TrainingCenterReview,
  ModerationAction 
} from '../../services/moderation.service';
import { useAuth } from '../../context/AuthContext';

type ReviewType = 'training' | 'training-center';
type ReviewStatus = 'all' | 'approved' | 'pending' | 'flagged' | 'rejected';

const ModerationPage: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<ReviewType>('training');
  const [stats, setStats] = useState<ModerationStats | null>(null);
  const [trainingReviews, setTrainingReviews] = useState<TrainingReview[]>([]);
  const [trainingCenterReviews, setTrainingCenterReviews] = useState<TrainingCenterReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<ReviewStatus>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedReview, setSelectedReview] = useState<TrainingReview | TrainingCenterReview | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [moderationNotes, setModerationNotes] = useState('');
  const [isModerating, setIsModerating] = useState(false);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  useEffect(() => {
    loadStats();
    loadReviews();
  }, [activeTab, currentPage, statusFilter, searchTerm]);

  const loadStats = async () => {
    try {
      const statsData = await moderationService.getModerationStats();
      setStats(statsData);
    } catch (error) {
    }
  };

  const loadReviews = async () => {
    try {
      setLoading(true);
      if (activeTab === 'training') {
        const response = await moderationService.getTrainingReviews(
          currentPage,
          10,
          statusFilter,
          searchTerm || undefined
        );
        setTrainingReviews(response.reviews as TrainingReview[]);
        setTotalPages(response.totalPages);
      } else {
        const response = await moderationService.getTrainingCenterReviews(
          currentPage,
          10,
          statusFilter,
          searchTerm || undefined
        );
        setTrainingCenterReviews(response.reviews as TrainingCenterReview[]);
        setTotalPages(response.totalPages);
      }
    } catch (error) {
      showNotification('error', 'Erreur lors du chargement des avis');
    } finally {
      setLoading(false);
    }
  };

  const handleModerate = async (reviewId: number, action: 'approve' | 'reject' | 'flag') => {
    if (!user?.id) return;

    try {
      setIsModerating(true);
      const moderationAction: ModerationAction = {
        action,
        notes: moderationNotes,
        adminId: parseInt(user.id),
      };

      if (activeTab === 'training') {
        await moderationService.moderateTrainingReview(reviewId, moderationAction);
      } else {
        await moderationService.moderateTrainingCenterReview(reviewId, moderationAction);
      }

      showNotification('success', `Avis ${action === 'approve' ? 'approuvé' : action === 'reject' ? 'rejeté' : 'signalé'} avec succès`);
      setIsModalOpen(false);
      setModerationNotes('');
      loadReviews();
      loadStats();
    } catch (error) {
      showNotification('error', 'Erreur lors de la modération');
    } finally {
      setIsModerating(false);
    }
  };

  const handleDelete = async (reviewId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet avis ?')) return;

    try {
      if (activeTab === 'training') {
        await moderationService.deleteTrainingReview(reviewId);
      } else {
        await moderationService.deleteTrainingCenterReview(reviewId);
      }

      showNotification('success', 'Avis supprimé avec succès');
      loadReviews();
      loadStats();
    } catch (error) {
      showNotification('error', 'Erreur lors de la suppression');
    }
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const getStatusBadge = (review: TrainingReview | TrainingCenterReview) => {
    if (review.isFlagged) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <Flag className="w-3 h-3 mr-1" />
          Signalé
        </span>
      );
    }
    if (review.isApproved) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Approuvé
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <AlertCircle className="w-3 h-3 mr-1" />
        En attente
      </span>
    );
  };

  const getStatusIcon = (status: ReviewStatus) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      case 'flagged':
        return <Flag className="w-4 h-4" />;
      case 'pending':
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Shield className="w-4 h-4" />;
    }
  };

  const currentReviews = activeTab === 'training' ? trainingReviews : trainingCenterReviews;

  return (
    <div className="p-6 bg-brand-surface min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Shield className="h-8 w-8 text-brand-primary" />
              Modération des Avis
            </h1>
            <p className="text-gray-600 mt-2">
              Gérez et modérez les avis des formations et centres de formation
            </p>
          </div>
          <button
            onClick={loadReviews}
            className="flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avis Formations</p>
                <p className="text-2xl font-bold text-gray-900">{stats.trainingReviews.total}</p>
              </div>
              <BookOpen className="h-8 w-8 text-blue-500" />
            </div>
            <div className="mt-4 flex gap-2 text-xs">
              <span className="text-green-600">{stats.trainingReviews.approved} approuvés</span>
              <span className="text-red-600">{stats.trainingReviews.flagged} signalés</span>
              <span className="text-yellow-600">{stats.trainingReviews.pending} en attente</span>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avis Centres</p>
                <p className="text-2xl font-bold text-gray-900">{stats.trainingCenterReviews.total}</p>
              </div>
              <Building className="h-8 w-8 text-purple-500" />
            </div>
            <div className="mt-4 flex gap-2 text-xs">
              <span className="text-green-600">{stats.trainingCenterReviews.approved} approuvés</span>
              <span className="text-red-600">{stats.trainingCenterReviews.flagged} signalés</span>
              <span className="text-yellow-600">{stats.trainingCenterReviews.pending} en attente</span>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Signalés</p>
                <p className="text-2xl font-bold text-red-600">
                  {stats.trainingReviews.flagged + stats.trainingCenterReviews.flagged}
                </p>
              </div>
              <Flag className="h-8 w-8 text-red-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">En Attente</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {stats.trainingReviews.pending + stats.trainingCenterReviews.pending}
                </p>
              </div>
              <AlertCircle className="h-8 w-8 text-yellow-500" />
            </div>
          </div>
        </div>
      )}

      {/* Tabs and Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('training')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'training'
                  ? 'border-brand-primary text-brand-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BookOpen className="w-4 h-4 inline mr-2" />
              Avis Formations
            </button>
            <button
              onClick={() => setActiveTab('training-center')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'training-center'
                  ? 'border-brand-primary text-brand-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Building className="w-4 h-4 inline mr-2" />
              Avis Centres de Formation
            </button>
          </nav>
        </div>

        <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Rechercher dans les avis..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as ReviewStatus)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              >
                <option value="all">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="approved">Approuvés</option>
                <option value="flagged">Signalés</option>
                <option value="rejected">Rejetés</option>
              </select>
            </div>
          </div>

          {/* Reviews Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {activeTab === 'training' ? 'Formation' : 'Centre'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Note
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commentaire
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center">
                        <RefreshCw className="w-6 h-6 animate-spin text-brand-primary mr-2" />
                        Chargement...
                      </div>
                    </td>
                  </tr>
                ) : currentReviews.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      Aucun avis trouvé
                    </td>
                  </tr>
                ) : (
                  currentReviews.map((review) => (
                    <tr key={review.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-brand-primary/10 flex items-center justify-center">
                              <User className="h-5 w-5 text-brand-primary" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {review.userName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {review.userEmail}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {activeTab === 'training' 
                            ? (review as TrainingReview).trainingTitle
                            : (review as TrainingCenterReview).trainingCenterName
                          }
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Star className="w-4 h-4 text-yellow-400 mr-1" />
                          <span className="text-sm font-medium text-gray-900">
                            {review.rating}/5
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 max-w-xs truncate">
                          {review.comment}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(review)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(review.createdAt).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              setSelectedReview(review);
                              setIsModalOpen(true);
                            }}
                            className="text-brand-primary hover:text-brand-primary/80"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleModerate(review.id, 'approve')}
                              className="text-green-600 hover:text-green-800"
                              title="Approuver"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleModerate(review.id, 'flag')}
                              className="text-red-600 hover:text-red-800"
                              title="Signaler"
                            >
                              <Flag className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(review.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-700">
                Page {currentPage} sur {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Précédent
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Review Detail Modal */}
      {isModalOpen && selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Détails de l'avis</h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Utilisateur</label>
                  <p className="text-sm text-gray-900">{selectedReview.userName}</p>
                  <p className="text-xs text-gray-500">{selectedReview.userEmail}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Note</label>
                  <div className="flex items-center">
                    <Star className="w-4 h-4 text-yellow-400 mr-1" />
                    <span className="text-sm font-medium">{selectedReview.rating}/5</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Commentaire</label>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                  {selectedReview.comment}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Notes de modération</label>
                <textarea
                  value={moderationNotes}
                  onChange={(e) => setModerationNotes(e.target.value)}
                  placeholder="Ajoutez des notes de modération..."
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => handleModerate(selectedReview.id, 'approve')}
                  disabled={isModerating}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  <CheckCircle className="w-4 h-4" />
                  Approuver
                </button>
                <button
                  onClick={() => handleModerate(selectedReview.id, 'flag')}
                  disabled={isModerating}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  <Flag className="w-4 h-4" />
                  Signaler
                </button>
                <button
                  onClick={() => handleModerate(selectedReview.id, 'reject')}
                  disabled={isModerating}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50"
                >
                  <XCircle className="w-4 h-4" />
                  Rejeter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 ${
          notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default ModerationPage;
